import React from "react";
import { Zoom, Typography, Box, Link } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useParams, useHistory } from "react-router-dom";
import { usePosts } from "../../context/PostsContext";

const useStyles = makeStyles(() => ({
  root: {
    marginTop: 24,
  },
  backLink: {
    marginBottom: 24,
    display: "inline-block",
    cursor: "pointer",
  },
  date: {
    marginBottom: 8,
  },
  title: {
    marginBottom: 16,
  },
  content: {
    whiteSpace: "pre-wrap",
    lineHeight: 1.7,
    "& strong": {
      fontWeight: 700,
    },
    "& a": {
      fontWeight: 500,
    },
  },
  divider: {
    border: 0,
    borderTop: "1px solid rgba(0,0,0,0.12)",
    margin: "28px 0",
  },
  codeBlock: {
    display: "block",
    padding: 16,
    marginTop: 12,
    marginBottom: 12,
    backgroundColor: "rgba(0,0,0,0.06)",
    borderRadius: 4,
    overflow: "auto",
    fontFamily: "monospace",
    fontSize: 13,
    lineHeight: 1.5,
    whiteSpace: "pre",
    border: "1px solid rgba(0,0,0,0.08)",
  },
  postImage: {
    maxWidth: "100%",
    height: "auto",
    marginTop: 16,
    marginBottom: 16,
    display: "block",
    borderRadius: 4,
  },
}));

const BlogPostView = () => {
  const classes = useStyles();
  const { postId } = useParams<{ postId: string }>();
  const history = useHistory();
  const { posts } = usePosts();
  const byId = posts.find((item) => item.id === postId);
  const index = parseInt(postId ?? "", 10);
  const post =
    byId ||
    (!isNaN(index) && index >= 0 && index < posts.length ? posts[index] : null);

  if (!post || typeof post !== "object") {
    return (
      <div className={classes.root}>
        <Link
          component="button"
          variant="body2"
          onClick={() => history.push("/notes")}
          className={classes.backLink}
        >
          ← Back to Notes
        </Link>
        <Typography color="textSecondary">Post not found.</Typography>
      </div>
    );
  }

  const renderInline = (text: string, keyPrefix: string) => {
    const tokens = text.split(
      /(\[[^\]]+\]\([^)]+\)|https?:\/\/[^\s)]+)/g
    );
    return tokens.map((token, i) => {
      const markdownLink = token.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      if (markdownLink) {
        return (
          <Link
            key={`${keyPrefix}-${i}`}
            href={markdownLink[2]}
            target="_blank"
            rel="noopener noreferrer"
            color="primary"
          >
            {markdownLink[1]}
          </Link>
        );
      }
      if (/^https?:\/\//.test(token)) {
        return (
          <Link
            key={`${keyPrefix}-${i}`}
            href={token}
            target="_blank"
            rel="noopener noreferrer"
            color="primary"
          >
            {token}
          </Link>
        );
      }
      return token.split(/(\*\*[^*]+\*\*)/g).map((part, j) =>
        part.startsWith("**") && part.endsWith("**") ? (
          <strong key={`${keyPrefix}-${i}-${j}`}>{part.slice(2, -2)}</strong>
        ) : (
          part
        )
      );
    });
  };

  const renderParagraph = (text: string, key: number) => {
    const trimmed = text.trim();
    if (!trimmed) return null;
    if (trimmed === "---") {
      return <hr key={key} className={classes.divider} />;
    }
    if (trimmed.startsWith("[IMAGE]")) {
      const src = trimmed.replace(/^\[IMAGE\]/, "").trim();
      if (src) {
        return (
          <img
            key={key}
            src={src}
            alt=""
            className={classes.postImage}
          />
        );
      }
      return null;
    }
    const isBoldHeader =
      /^\*\*[^*]+\*\*$/.test(trimmed) ||
      (trimmed.startsWith("**") && !trimmed.includes("]("));
    if (isBoldHeader) {
      return (
        <Typography key={key} variant="h2" style={{ marginTop: 16, marginBottom: 8, fontWeight: 700 }}>
          {trimmed.replace(/\*\*/g, "")}
        </Typography>
      );
    }
    return (
      <Typography
        key={key}
        variant="body1"
        paragraph
        className={classes.content}
        style={{ marginBottom: 12 }}
      >
        {renderInline(trimmed, `p${key}`)}
      </Typography>
    );
  };

  const parts: React.ReactNode[] = [];
  const rawContent = typeof post.content === "string" ? post.content : "";
  const segments = rawContent.split(/\n?```\n?/);
  let keyIdx = 0;
  segments.forEach((segment, i) => {
    if (i % 2 === 1) {
      parts.push(
        <pre key={keyIdx++} className={classes.codeBlock}>
          {segment.trim()}
        </pre>
      );
    } else {
      segment
        .split(/\n\n+/)
        .forEach((paragraph) => {
          const node = renderParagraph(paragraph, keyIdx++);
          if (node) parts.push(node);
        });
    }
  });
  const contentWithParagraphs = parts.length > 0 ? parts : null;

  return (
    <Zoom in={true} style={{ transitionDelay: "150ms" }}>
      <div className={classes.root}>
        <Link
          component="button"
          variant="body2"
          onClick={() => history.push("/notes")}
          className={classes.backLink}
          color="primary"
        >
          ← Back to Notes
        </Link>
        <Typography variant="body2" color="textSecondary" className={classes.date}>
          {post.date}
        </Typography>
        <Typography variant="h1" className={classes.title}>
          {post.title}
        </Typography>
        {contentWithParagraphs ? (
          <Box className={classes.content}>{contentWithParagraphs}</Box>
        ) : (
          <Typography color="textSecondary">No content for this post.</Typography>
        )}
      </div>
    </Zoom>
  );
};
export default BlogPostView;
